import {
  TrendingUp,
  TrendingDown,
  MapPin,
  DollarSign,
  AlertCircle,
  UserCheck,
  RefreshCw,
  GraduationCap,
  FileCheck,
  Repeat,
  ClipboardList,
  LogOut,
} from "lucide-react";
import { localizeWorkflowStepName } from "../../../utils/workflowStepNames";

// ─── Action Type Definitions ─────────────────────────────────────────────────
// Each entry is consumed by ActionModal, ConfirmModal, HistoryModal,
// PendingApprovalsModal, EmployeeTable, and the main EmployeeActionsPage.
//
// Shape:
//   id               – stored as `actionType` in the DB
//   labelKey         – key into translations (e.g. "promoteEmployee")
//   descriptionKey   – key into translations for the short description
//   icon             – Lucide icon component
//   color            – gradient string used for buttons / headers
//   bgColor          – translucent background for badges
//   borderColor      – border for badges
//   requiresApproval – whether the action enters the approval workflow
//   approvalLevelKey – fallback translation key (used when no workflow definition is loaded)

export const ACTION_TYPES = [
  {
    id: "promote",
    labelKey: "promoteEmployee",
    descriptionKey: "advanceToHigher",
    icon: TrendingUp,
    color: "linear-gradient(135deg, #10b981, #059669)",
    bgColor: "rgba(16, 185, 129, 0.15)",
    borderColor: "rgba(16, 185, 129, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "demote",
    labelKey: "demoteEmployee",
    descriptionKey: "moveToLower",
    icon: TrendingDown,
    color: "linear-gradient(135deg, #f59e0b, #d97706)",
    bgColor: "rgba(245, 158, 11, 0.15)",
    borderColor: "rgba(245, 158, 11, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "transfer",
    labelKey: "transferEmployee",
    descriptionKey: "transferDifferent",
    icon: MapPin,
    color: "linear-gradient(135deg, #3b82f6, #2563eb)",
    bgColor: "rgba(59, 130, 246, 0.15)",
    borderColor: "rgba(59, 130, 246, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "salary_change",
    labelKey: "salaryAdjustment",
    descriptionKey: "modifyCompensation",
    icon: DollarSign,
    color: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
    bgColor: "rgba(139, 92, 246, 0.15)",
    borderColor: "rgba(139, 92, 246, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "terminate",
    labelKey: "terminateEmployment",
    descriptionKey: "endEmployment",
    icon: AlertCircle,
    color: "linear-gradient(135deg, #ef4444, #dc2626)",
    bgColor: "rgba(239, 68, 68, 0.15)",
    borderColor: "rgba(239, 68, 68, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "resignation",
    labelKey: "resignationAction",
    descriptionKey: "resignationDescription",
    icon: LogOut,
    color: "linear-gradient(135deg, #f43f5e, #e11d48)",
    bgColor: "rgba(244, 63, 94, 0.15)",
    borderColor: "rgba(244, 63, 94, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "suspend",
    labelKey: "suspendEmployee",
    descriptionKey: "temporarySuspension",
    icon: AlertCircle,
    color: "linear-gradient(135deg, #f97316, #ea580c)",
    bgColor: "rgba(249, 115, 22, 0.15)",
    borderColor: "rgba(249, 115, 22, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "rehire",
    labelKey: "rehireEmployee",
    descriptionKey: "reinstateFormer",
    icon: UserCheck,
    color: "linear-gradient(135deg, #06b6d4, #0891b2)",
    bgColor: "rgba(6, 182, 212, 0.15)",
    borderColor: "rgba(6, 182, 212, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  /*
  {
    id: "contract_update",
    labelKey: "contractUpdate",
    descriptionKey: "modifyContract",
    icon: FileCheck,
    color: "linear-gradient(135deg, #6366f1, #4f46e5)",
    bgColor: "rgba(99, 102, 241, 0.15)",
    borderColor: "rgba(99, 102, 241, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "rotation",
    labelKey: "jobRotation",
    descriptionKey: "rotateDifferent",
    icon: Repeat,
    color: "linear-gradient(135deg, #14b8a6, #0d9488)",
    bgColor: "rgba(20, 184, 166, 0.15)",
    borderColor: "rgba(20, 184, 166, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  }
    */
];

// ─── Action ID → Workflow subType map ────────────────────────────────────────
// Used by getWorkflowApprovalLabel to look up the correct WorkflowDefinition.
// Values must match WorkflowsTab ACTION_SUBTYPE_OPTIONS.

export const ACTION_SUBTYPE_MAP = {
  promote: "PROMOTION",
  demote: "DEMOTION",
  transfer: "TRANSFER",
  salary_change: "SALARY_ADJUSTMENT",
  terminate: "TERMINATION_EMPLOYER",
  resignation: "RESIGNATION",
  suspend: "SUSPENSION",
  rehire: "REHIRE",
  contract_update: "CONTRACT",
  rotation: "ROTATION",
};

// ─── Human-readable assignee type labels ─────────────────────────────────────
const ASSIGNEE_LABELS = {
  DIRECT_MANAGER: { en: "Direct Manager", ar: "مدير مباشر" },
  SECOND_MANAGER: { en: "Second Manager", ar: "مدير ثانٍ" },
  DEPARTMENT_MANAGER: { en: "Dept. Manager", ar: "مدير القسم" },
  ADMIN_LEVEL: { en: "Admin Level", ar: "مستوى إداري" },
  ADMIN_GENERAL: { en: "Any Admin", ar: "أي إداري" },
  EXPLICIT: { en: "Specific Assignee", ar: "موظف محدد" },
};

/**
 * Resolves the WorkflowDefinition that actually applies to a given action +
 * department, mirroring the backend's department-aware fallback chain in
 * workflowService.js (resolveWorkflowDefinition): an exact department match
 * wins over the company-wide "__default__" definition. Without this, a
 * department that has its own configured approval chain (set from the
 * Configuration → Workflows tab) would never show up here — the frontend
 * would always render the company-wide default instead, even though the
 * backend would actually route the request through the department-specific
 * chain once submitted.
 *
 * @param {string}   actionId            – e.g. "promote"
 * @param {Array}    workflowDefinitions – array returned by listWorkflowDefinitions()
 * @param {string}   [departmentName]    – the target employee's department name
 * @returns {object|null}
 */
export function resolveActionWorkflowDefinition(actionId, workflowDefinitions, departmentName) {
  const subType = ACTION_SUBTYPE_MAP[actionId];
  if (!subType || !Array.isArray(workflowDefinitions)) return null;

  const forSubtype = workflowDefinitions.filter(
    (def) => def.module === "ACTION" && def.subType === subType
  );
  if (forSubtype.length === 0) return null;

  const dept = (departmentName || "").trim();

  // 1. Exact department match (if we know the employee's department)
  if (dept) {
    const exact = forSubtype.find(
      (def) => (def.targetEmployeeType || "").trim().toLowerCase() === dept.toLowerCase()
    );
    if (exact) return exact;
  }

  // 2. Company-wide default
  const companyWide = forSubtype.find(
    (def) => !def.targetEmployeeType || def.targetEmployeeType === "__default__"
  );
  if (companyWide) return companyWide;

  // 3. Anything else configured for this subtype
  return forSubtype[0];
}

/**
 * Derives a human-readable approval-chain label from loaded workflow definitions
 * for a given action ID. Department-aware — see resolveActionWorkflowDefinition().
 *
 * @param {string}   actionId           – e.g. "promote"
 * @param {Array}    workflowDefinitions – array returned by listWorkflowDefinitions()
 * @param {string}   language            – "ar" | "en"
 * @param {object}   [t]                – translations object (used for the fallback key)
 * @param {string}   [fallbackKey]      – translation key to use when no definition found
 * @param {string}   [departmentName]   – the target employee's department name
 * @returns {string}
 */
export function getWorkflowApprovalLabel(
  actionId,
  workflowDefinitions,
  language,
  t,
  fallbackKey,
  departmentName
) {
  const resolvedDef = resolveActionWorkflowDefinition(actionId, workflowDefinitions, departmentName);

  if (resolvedDef && Array.isArray(resolvedDef.steps) && resolvedDef.steps.length > 0) {
    const lang = language === "ar" ? "ar" : "en";

    // Build "Step 1 (Direct Manager) → Step 2 (Admin Level)" style label
    const parts = resolvedDef.steps
      .sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0))
      .map((step) => {
        const assigneeLabel =
          ASSIGNEE_LABELS[step.assigneeType]?.[lang] || step.assigneeType || "";
        const stepName = localizeWorkflowStepName(step.stepName, lang);
        return stepName ? `${stepName} (${assigneeLabel})` : assigneeLabel;
      });

    return parts.join(language === "ar" ? " ← " : " → ");
  }

  // Fallback: use the static translation key
  if (t && fallbackKey) return t[fallbackKey] || fallbackKey;
  return "";
}

/**
 * Builds a "Step 2 of 3 — Second Line Manager Approval" style label for the
 * LIVE, actual current step of a specific pending action — as opposed to
 * getWorkflowApprovalLabel() above, which only ever renders the static
 * definition chain and has no idea which step a given request is really
 * sitting at.
 *
 * Two sources are checked, in order:
 *  1. `pendingWorkflowSteps` — steps assigned to the current viewer for this
 *     action (from the unified /workflows/pending inbox). Present whenever
 *     the viewer is the actual assignee of the live step.
 *  2. `action.workflowProgress` — server-computed live-step info attached to
 *     every action row by GET /employee-actions (added specifically so this
 *     works even when the viewer isn't the step's assignee, e.g. an admin
 *     looking at a step still sitting with a first-line manager).
 *
 * @param {object}  action               – the pending action row
 * @param {Array}   [pendingWorkflowSteps] – wf steps for this action from pendingWorkflows[action.id]
 * @param {string}  language
 * @param {object}  t
 * @returns {string|null} null when no live step info is available at all
 */
export function getCurrentStepLabel(action, pendingWorkflowSteps, language, t) {
  const fromViewerInbox = Array.isArray(pendingWorkflowSteps)
    ? pendingWorkflowSteps.find((s) => String(s?.status || "").trim().toUpperCase() === "PENDING")
    : null;

  const stepName = localizeWorkflowStepName(
    fromViewerInbox?.stepName || action?.workflowProgress?.currentStepName,
    language
  );
  const stepOrder = fromViewerInbox?.stepOrder ?? action?.workflowProgress?.currentStepOrder;
  const totalSteps = fromViewerInbox?.totalSteps || action?.workflowProgress?.totalSteps;

  if (!stepName && stepOrder == null) return null;

  const displayStepName = stepName || (language === "ar" ? `الخطوة ${stepOrder}` : `Step ${stepOrder}`);
  const ofText = language === "ar" ? "من" : (t?.stepOfLabel || "of");

  const orderPart =
    stepOrder != null && totalSteps
      ? ` (${stepOrder} ${ofText} ${totalSteps})`
      : stepOrder != null
      ? ` (${language === "ar" ? `الخطوة ${stepOrder}` : `Step ${stepOrder}`})`
      : "";

  return `${displayStepName}${orderPart}`;
}

// ─── Action → Feature Permission Map ────────────────────────────────────────
// Maps each action id to the feature key checked in
// `permissions.features.changesPage` for admin-level gating.
// Keys MUST match the backend permission registry
// (user-service/src/config/permissionRegistry.js → page "changesPage").

export const ACTION_FEATURE_MAP = {
  promote: "promoteemployee",
  demote: "demoteemployee",
  transfer: "transferemployee",
  salary_change: "salaryadjustment",
  terminate: "terminateemployment",
  resignation: "resignemployee",
  suspend: "suspendemployee",
  rehire: "rehireemployee",
  contract_update: "contractupdate",
  rotation: "jobrotation",
};

// ─── Actions hidden in MANAGER view ─────────────────────────────────────────
// Mirrors the backend MANAGER_ACTIONS.changesPage whitelist
// (user-service/src/middleware/permissionCheck.js): these action types are
// not whitelisted for a manager in manager view, so the API would answer 403.
// Salary changes are admin / admin-level only.
export const MANAGER_BLOCKED_ACTIONS = ["salary_change"];

export const isActionBlockedForManagerView = (actionId, { isAdminView, isAdminRole } = {}) =>
  !isAdminView && !isAdminRole && MANAGER_BLOCKED_ACTIONS.includes(actionId);
